import { ReactNode } from "react";

import { useIntl } from "react-intl";

import { PRICE_FINDER_CONTAINER } from "@/analytics/mappings/container.ts";
import { useTryIntent } from "@/components/try-intent/src/use-try-intent.ts";
import { PricingCard } from "@/libraries/design/components/pricing-card/src/pricing-card.tsx";
import { DataEventProps } from "@/libraries/wpl/foundations/src/index.ts";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag.ts";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest.ts";

import { useCalculationState } from "../calculation.ts";
import {
    PriceAltContainer,
    MonthlyEnterpriseMessage,
    getFeaturesList,
    UserTier,
    UserTierProps,
    UserTierExpanded,
    useLocalizedCtaUrl,
    useConfluenceDirectBuyExperiment,
    ConfluenceDirectBuyButton,
    ConfluenceDirectBuyButtonProps
} from "../components";
import { useBillingCycle, useSelector } from "../hooks.ts";
import * as messages from "../messages.ts";
import { getCurrencyIsoCode } from "../selectors.ts";
import { Plan } from "../types.ts";

export interface ProductPriceCardProps {
    planData: Plan;
    trackingProductKey: string;
    topSectionStyle?: React.CSSProperties;
    bottomSectionStyle?: React.CSSProperties;
    showBannerText?: boolean;
    customBannerText?: ReactNode;
    isSingleCard?: boolean;
    monthlyPerUserLabel?: string;
    tierLabelType?: UserTierProps["unitType"];
    omitRange?: boolean;
    hasRangeSlot?: boolean;
    priceAltElementForAboveMaxThreshold?: ReactNode;
}

type TrackingDataProductKey = DataEventProps["data-product-key"];

export interface UsePriceCardTryIntentProps {
    productKey: string;
    edition: string;
}

function usePriceCardTryIntent(props: UsePriceCardTryIntentProps) {
    const { productKey, edition } = props;
    return useTryIntent({
        containerId: PRICE_FINDER_CONTAINER,
        intentType: "button",
        productKey,
        edition
    });
}

const FREE_EDITION = "free";

// the dataEventProps are added to the button props for these cards to automatically register
// analytics events, on top of the manual analytics events that we're calling; the events are
// duplicative, but we're keeping them for now to prevent regressions when comparing to the
// migrated page
export const FreePriceCard = (props: ProductPriceCardProps) => {
    const { planData, topSectionStyle, bottomSectionStyle, customBannerText } =
        props;
    const fields = planData.fields;
    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const tryIntent = usePriceCardTryIntent({
        productKey: props.trackingProductKey,
        edition: FREE_EDITION
    });
    const dataEventPropsLabel = `${FREE_EDITION}-get-it-now`;

    const { renderDirectBuyMatchSpace } = useConfluenceDirectBuyExperiment({
        sku: fields.sku
    });

    const addSignupSourceToUrlsOnPricingPageEnabled = checkFeatureFlagGate(
        featureFlags.addSignupSourceToUrlsOnPricingPage
    );
    const signupUrlSourceQueryParam = addSignupSourceToUrlsOnPricingPageEnabled
        ? "&signupSource=pricing"
        : "";

    return (
        <PricingCard
            hasBannerSlot
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (state.recommended && intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={state.price}
            hasUnitSlot
            hasRangeSlot={
                props.hasRangeSlot !== undefined
                    ? props.hasRangeSlot
                    : state.billingCycle === "ANNUAL"
            }
            buttonText={cta?.ctaButtonText}
            buttonVariant={state.recommended ? "primary" : "secondary"}
            buttonHref={`${ctaUrl}${signupUrlSourceQueryParam}`}
            buttonProps={{
                onClick: tryIntent.onClick,
                onKeyDown: tryIntent.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as TrackingDataProductKey,
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            hasLinkSlot={renderDirectBuyMatchSpace}
            benefitsTitle={fields.highlightedFeaturesTitle}
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};

const STANDARD_EDITION = "standard";
const PREMIUM_EDITION = "premium";
export const StandardPriceCard = (props: ProductPriceCardProps) => (
    <CommonPriceCard {...props} edition={STANDARD_EDITION} />
);
export const PremiumPriceCard = (props: ProductPriceCardProps) => (
    <CommonPriceCard {...props} edition={PREMIUM_EDITION} />
);

interface CommonPriceCardProps extends ProductPriceCardProps {
    edition: string;
    priceAltElementForAboveMaxThreshold?: ReactNode;
}
// eslint-disable-next-line complexity
const CommonPriceCard = (props: CommonPriceCardProps) => {
    const {
        planData,
        topSectionStyle,
        bottomSectionStyle,
        tierLabelType,
        customBannerText
    } = props;
    const fields = planData.fields;
    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);
    const dataEventPropsLabel = `${props.edition}-start-trial`;

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const tryIntent = usePriceCardTryIntent({
        productKey: props.trackingProductKey,
        edition: props.edition
    });

    const { renderDirectBuyBtn } = useConfluenceDirectBuyExperiment({
        sku: fields.sku
    });

    const priceAltElement = (() => {
        if (
            state.isUnitCountAboveMaxThreshold &&
            props.priceAltElementForAboveMaxThreshold !== undefined
        ) {
            return props.priceAltElementForAboveMaxThreshold;
        }
        return null;
    })();

    const addSignupSourceToUrlsOnPricingPageEnabled = checkFeatureFlagGate(
        featureFlags.addSignupSourceToUrlsOnPricingPage
    );
    const signupUrlSourceQueryParam = addSignupSourceToUrlsOnPricingPageEnabled
        ? "&signupSource=pricing"
        : "";
    return (
        <PricingCard
            priceTestid={
                state.billingCycle === "ANNUAL"
                    ? "annual-price"
                    : "monthly-price"
            }
            hasRangeSlot={
                state.isUnitCountAboveMaxThreshold &&
                currencyIsoCode === "JPY" &&
                state.billingCycle === "ANNUAL"
            }
            hasBannerSlot
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (state.recommended && intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={!state.isUnitCountAboveMaxThreshold && state.price}
            priceAltElement={
                state.isUnitCountAboveMaxThreshold &&
                props.priceAltElementForAboveMaxThreshold !== undefined ? (
                    priceAltElement
                ) : (
                    <PriceAltContainer>
                        {intl.formatMessage(
                            messages.aboveUnitMaxThreshold.default
                        )}
                    </PriceAltContainer>
                )
            }
            unit={
                state.isUnitCountAboveMaxThreshold
                    ? undefined
                    : state.billingCycle === "MONTHLY"
                    ? props.monthlyPerUserLabel ??
                      intl.formatMessage(messages.perUserPerMonth)
                    : intl.formatMessage(messages.perYear)
            }
            hasUnitSlot
            range={
                state.billingCycle === "ANNUAL" &&
                !props.omitRange &&
                !state.isUnitCountAboveMaxThreshold && (
                    <UserTier
                        // @ts-ignore
                        unitLimitLower={state.unitTierLowerBound}
                        // @ts-ignore
                        unitLimitUpper={state.unitTierUpperBound}
                        unitType={tierLabelType}
                    />
                )
            }
            tooltipContent={
                <UserTierExpanded
                    // @ts-ignore
                    unitLimitLower={state.unitTierLowerBound}
                    // @ts-ignore
                    unitLimitUpper={state.unitTierUpperBound}
                    unitsInTier={state.unitCount}
                    unitsAvailableInTier={state.unitsAvailableInTier}
                />
            }
            buttonText={cta?.ctaButtonText}
            buttonHref={`${ctaUrl}${signupUrlSourceQueryParam}`}
            buttonDisabled={state.isUnitCountAboveMaxThreshold}
            buttonVariant={
                state.recommended || props.isSingleCard
                    ? "primary"
                    : "secondary"
            }
            buttonProps={{
                onClick: tryIntent.onClick,
                onKeyDown: tryIntent.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as DataEventProps["data-product-key"],
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            link={
                renderDirectBuyBtn && (
                    <ConfluenceDirectBuyButton
                        sku={fields.sku}
                        edition={
                            props.edition as ConfluenceDirectBuyButtonProps["edition"]
                        }
                    />
                )
            }
            benefitsTitle={fields.highlightedFeaturesTitle}
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};

export interface EnterprisePriceCardProps extends ProductPriceCardProps {
    isJpyPriceEnabled?: boolean;
    priceAltElement?: ReactNode;
}
// eslint-disable-next-line complexity
export const EnterprisePriceCard = (props: EnterprisePriceCardProps) => {
    const {
        planData,
        topSectionStyle,
        bottomSectionStyle,
        showBannerText = true,
        isSingleCard,
        tierLabelType,
        customBannerText,
        isJpyPriceEnabled
    } = props;
    const fields = planData.fields;

    const state = useCalculationState({ offeringSku: fields.sku });
    const intl = useIntl();
    const currencyIsoCode = useSelector(getCurrencyIsoCode);

    const [_, changeBillingCycle] = useBillingCycle();

    const priceAltElement = (() => {
        if (props.priceAltElement !== undefined) {
            return props.priceAltElement;
        }

        if (currencyIsoCode === "JPY" && !isJpyPriceEnabled) {
            return (
                <PriceAltContainer>
                    {intl.formatMessage(messages.enterpriseJpyMessage)}
                </PriceAltContainer>
            );
        }

        if (state.billingCycle === "MONTHLY") {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "24px" }}>
                    <MonthlyEnterpriseMessage
                        switchToAnnual={() => changeBillingCycle("ANNUAL")}
                    />
                </PriceAltContainer>
            );
        }
        if (state.isUnitCountAboveMaxThreshold) {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "48px" }}>
                    {intl.formatMessage(
                        messages.aboveUnitMaxThreshold.cloudEnterprise
                    )}
                </PriceAltContainer>
            );
        }
        if (state.isUnitCountBelowMinThreshold) {
            return (
                <PriceAltContainer style={{ paddingBlockEnd: "48px" }}>
                    {intl.formatMessage(
                        messages.belowUnitMinThreshold.cloudEnterprise,
                        {
                            minUnitCount: state.unitCountMin
                        }
                    )}
                </PriceAltContainer>
            );
        }
        return null;
    })();

    const isPriceShown =
        !state.isUnitCountAboveMaxThreshold && !priceAltElement;

    let price: string | undefined = undefined;
    let range: ReactNode | undefined = undefined;
    let tooltipContent: ReactNode | undefined = undefined;
    if (isPriceShown) {
        price = state.price;
        range = (
            <UserTier
                // @ts-ignore
                unitLimitLower={state.unitTierLowerBound}
                // @ts-ignore
                unitLimitUpper={state.unitTierUpperBound}
                unitType={tierLabelType}
            />
        );
        tooltipContent = (
            <UserTierExpanded
                // @ts-ignore
                unitLimitLower={state.unitTierLowerBound}
                // @ts-ignore
                unitLimitUpper={state.unitTierUpperBound}
                unitsInTier={state.unitCount}
                unitsAvailableInTier={state.unitsAvailableInTier}
            />
        );
    }

    const cta = fields.microCta?.[0]?.fields;
    const ctaUrl = useLocalizedCtaUrl(cta);
    const edition = "enterprise";
    const tryIntent = usePriceCardTryIntent({
        productKey: props.trackingProductKey,
        edition
    });
    const dataEventPropsLabel = `${edition}-contact-sales`;

    const { renderDirectBuyMatchSpace } = useConfluenceDirectBuyExperiment({
        sku: fields.sku
    });

    return (
        <PricingCard
            priceTestid={
                state.billingCycle === "ANNUAL"
                    ? "annual-price"
                    : "monthly-price"
            }
            hasBannerSlot
            hasRangeSlot={
                currencyIsoCode === "JPY" && state.billingCycle === "ANNUAL"
            }
            topSectionStyle={topSectionStyle}
            bottomSectionStyle={bottomSectionStyle}
            bannerText={
                customBannerText ??
                (showBannerText &&
                    state.recommended &&
                    intl.formatMessage(messages.recommended))
            }
            title={fields.planTitleExternal}
            description={fields.planOneLiner}
            currencyIsoCode={currencyIsoCode}
            price={price}
            priceAltElement={priceAltElement}
            unit={
                // if a price alt element exists, there should be no unit
                priceAltElement
                    ? undefined
                    : !state.isUnitCountAboveMaxThreshold &&
                      !state.isUnitCountBelowMinThreshold
                    ? intl.formatMessage(messages.perYear)
                    : undefined
            }
            range={range}
            hasUnitSlot={
                // the unbounded text for the price alt element makes the unbounded
                currencyIsoCode === "JPY"
            }
            tooltipContent={tooltipContent}
            buttonText={cta?.ctaButtonText}
            buttonHref={ctaUrl}
            buttonVariant={
                state.recommended || isSingleCard ? "primary" : "secondary"
            }
            buttonProps={{
                onClick: tryIntent.onClick,
                onKeyDown: tryIntent.onKeyDown,
                dataEventProps: {
                    "data-product-key":
                        props.trackingProductKey as TrackingDataProductKey,
                    "data-event-component": "linkButton",
                    "data-event-container": PRICE_FINDER_CONTAINER,
                    "data-event": "clicked",
                    "data-label": `${dataEventPropsLabel}`,
                    "data-label-english": `${dataEventPropsLabel}`
                }
            }}
            hasLinkSlot={renderDirectBuyMatchSpace}
            benefitsTitle={fields.highlightedFeaturesTitle}
            // in order for us to render just the top section fo a pricing card, the contentful data must be empty/undefined
            benefits={getFeaturesList(fields.highlightedFeaturesContainer)}
        />
    );
};
